/**
 * Customer Management React Query Hooks
 * 
 * Provides React Query hooks for customer lifecycle management including:
 * - Fetching customers with filtering and pagination
 * - Creating, updating, and deleting customers
 * - Customer profile management
 * 
 * All hooks use React Query for automatic caching, background refetching,
 * and optimistic updates with proper cache invalidation.
 * 
 * @module hooks/useCustomers
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { customerService } from '../services/api';
import { API_ENDPOINTS } from '@/config/api.config.js';
import api from '@/services/api.js';

/**
 * Fetches customers with optional filtering
 * 
 * @param {Object} filters - Query parameters for filtering customers
 * @param {string} [filters.role] - Filter by user role
 * @param {string} [filters.status] - Filter by customer status
 * @param {string} [filters.search] - Search by name or email
 * @param {number} [filters.page] - Page number for pagination
 * @param {number} [filters.limit] - Items per page
 * @returns {Object} React Query result with customers data
 * @property {Object} data - Customers response object
 * @property {Array} data.items - Array of customer records
 * @property {number} data.total - Total number of customers
 * @property {boolean} isLoading - Loading state
 * @property {boolean} isError - Error state
 */
export const useCustomers = (filters = {}) => {
  return useQuery({
    queryKey: ['customers', filters],
    queryFn: async () => {
      try {
        return await customerService.getCustomers(filters);
      } catch {
        return { items: [], total: 0, customers: [] };
      }
    },
  });
};

/**
 * Fetches a single customer by ID
 * 
 * Query is disabled if customerId is not provided
 * 
 * @param {string} customerId - Customer ID
 * @returns {Object} React Query result with customer data
 * @property {Object} data - Customer record with full details
 * @property {boolean} isLoading - Loading state
 * @property {boolean} isError - Error state
 */
export const useCustomer = (customerId) => {
  return useQuery({
    queryKey: ['customers', customerId],
    queryFn: () => customerService.getCustomer(customerId),
    enabled: !!customerId,
  });
};

/**
 * Mutation hook to create a new customer
 * 
 * Automatically invalidates customers query on success
 * 
 * @returns {Object} React Query mutation result
 * @property {Function} mutate - Function to trigger mutation
 * @property {boolean} isPending - Mutation pending state
 * 
 * @example
 * const createCustomer = useCreateCustomer();
 * createCustomer.mutate({ 
 *   name: 'John Doe', 
 *   email: 'john@example.com',
 *   company: 'Acme Corp' 
 * });
 */
export const useCreateCustomer = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: customerService.createCustomer,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customers'] });
    },
  });
};

/**
 * Mutation hook to update an existing customer
 * 
 * Automatically invalidates both the specific customer and customers list on success
 * 
 * @returns {Object} React Query mutation result
 * @property {Function} mutate - Function to trigger mutation
 * @property {boolean} isPending - Mutation pending state
 * 
 * @example
 * const updateCustomer = useUpdateCustomer();
 * updateCustomer.mutate({ 
 *   customerId: '123', 
 *   data: { status: 'active' } 
 * });
 */
export const useUpdateCustomer = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ customerId, data }) => customerService.updateCustomer(customerId, data),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['customers', variables.customerId] });
      queryClient.invalidateQueries({ queryKey: ['customers'] });
    },
  });
};

/**
 * Mutation hook to delete a customer
 * 
 * Automatically invalidates customers query on success
 * 
 * @returns {Object} React Query mutation result
 * @property {Function} mutate - Function to trigger mutation
 * @property {boolean} isPending - Mutation pending state
 * 
 * @example
 * const deleteCustomer = useDeleteCustomer();
 * deleteCustomer.mutate('customer_123');
 */
export const useDeleteCustomer = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: customerService.deleteCustomer,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customers'] });
    },
  });
};

/**
 * Hook to register a new customer (self-registration)
 * @returns {Object} Mutation object for customer registration
 */
export const useRegisterCustomer = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (registrationData) => {
      const { data } = await api.post(`${API_ENDPOINTS.AUTH}/register`, registrationData);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customers'] });
    },
  });
};

/**
 * Fetches customer analytics
 * 
 * @param {string} customerId - Customer ID
 * @returns {Object} React Query result with analytics data
 */
export const useCustomerAnalytics = (customerId) => {
  return useQuery({
    queryKey: ['customers', 'analytics', customerId],
    queryFn: () => customerService.getAnalytics(customerId),
    enabled: !!customerId,
  });
};
